// src/components/attendance/AttendanceList.jsx
import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Calendar, 
  Clock, 
  TrendingUp, 
  Eye, 
  Edit, 
  Trash2,
  Plus,
  Filter,
  Download,
  Search,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { AttendanceService } from '../../services/attendanceService';
import { useAuth } from '../../contexts/AuthContext';

const AttendanceList = ({ onCreateNew, onEdit, onView }) => {
  const { userProfile } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    grade: '',
    subject: '',
    dateFrom: '',
    dateTo: '',
    searchTerm: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    if (sessions.length > 0) {
      loadAnalytics();
    }
  }, [sessions, filters]);

  const loadSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await AttendanceService.getAttendanceSessions(filters);
      setSessions(data);
    } catch (err) {
      console.error('Error loading attendance sessions:', err);
      setError('Failed to load attendance sessions');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const analyticsData = await AttendanceService.getAttendanceAnalytics(filters);
      setAnalytics(analyticsData);
    } catch (err) {
      console.error('Error loading analytics:', err);
    }
  };

  const handleDelete = async (sessionId, sessionName) => {
    if (window.confirm(`Are you sure you want to delete the session "${sessionName}"?`)) {
      try {
        await AttendanceService.deleteAttendanceSession(sessionId);
        loadSessions();
      } catch (err) {
        console.error('Error deleting session:', err);
        alert('Failed to delete session');
      }
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    loadSessions();
  };

  const clearFilters = () => {
    setFilters({
      grade: '',
      subject: '',
      dateFrom: '',
      dateTo: '',
      searchTerm: ''
    });
    setTimeout(() => loadSessions(), 100);
  };

  const exportToCSV = () => {
    if (sessions.length === 0) return;

    const headers = [
      'Date',
      'Time',
      'Grade',
      'Subject',
      'Duration',
      'Type',
      'Expected',
      'Actual',
      'Rate',
      'Engagement',
      'Topics',
      'Activities'
    ];

    const csvData = sessions.map(session => [
      new Date(session.sessionDate).toLocaleDateString(),
      session.sessionTime || '',
      session.grade,
      session.subject,
      session.duration || '',
      session.sessionType || '',
      session.expectedLearners,
      session.actualLearners,
      session.expectedLearners > 0 ? ((session.actualLearners / session.expectedLearners) * 100).toFixed(1) + '%' : '0%',
      session.learnerEngagement || '',
      session.topicsCovered?.join('; ') || '',
      session.activitiesConducted?.join('; ') || ''
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance_sessions_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Filter sessions based on search term
  const filteredSessions = sessions.filter(session => {
    if (!filters.searchTerm) return true;
    const searchLower = filters.searchTerm.toLowerCase();
    return (
      session.subject.toLowerCase().includes(searchLower) ||
      session.grade.toLowerCase().includes(searchLower) ||
      session.sessionType?.toLowerCase().includes(searchLower) ||
      session.topicsCovered?.some(topic => topic.toLowerCase().includes(searchLower)) ||
      session.notes?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Sessions</h3>
        <p className="text-gray-500 mb-6">{error}</p>
        <button
          onClick={loadSessions}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Class/Session Attendance</h2>
          <p className="text-gray-600">Record and track class attendance, engagement, and session details</p>
        </div>
        <button
          onClick={onCreateNew}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Record New Session
        </button>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              <h3 className="font-medium text-blue-800">Total Sessions</h3>
            </div>
            <p className="text-2xl font-bold text-blue-900">{analytics.totalSessions}</p>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <h3 className="font-medium text-green-800">Attendance Rate</h3>
            </div>
            <p className="text-2xl font-bold text-green-900">{analytics.attendanceRate.toFixed(1)}%</p>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-purple-600" />
              <h3 className="font-medium text-purple-800">Avg Attendance</h3>
            </div>
            <p className="text-2xl font-bold text-purple-900">{analytics.averageAttendance.toFixed(0)}</p>
          </div>
          
          <div className="bg-orange-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-orange-600" />
              <h3 className="font-medium text-orange-800">Engagement</h3>
            </div>
            <p className="text-2xl font-bold text-orange-900">
              {analytics.engagementAverage === 1 ? 'Low' :
               analytics.engagementAverage === 2 ? 'Medium' :
               analytics.engagementAverage === 3 ? 'High' : 'N/A'}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <Filter className="w-4 h-4" />
            Filters
            {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={filters.searchTerm}
                onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                placeholder="Search sessions..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
              <select
                value={filters.grade}
                onChange={(e) => handleFilterChange('grade', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Grades</option>
                {userProfile?.grades?.map(grade => (
                  <option key={grade} value={grade}>Grade {grade}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
              <select
                value={filters.subject}
                onChange={(e) => handleFilterChange('subject', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Subjects</option>
                {userProfile?.subjects?.map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-end gap-2">
              <button
                onClick={applyFilters}
                className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Apply
              </button>
              <button
                onClick={clearFilters}
                className="px-3 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Sessions List */}
      <div className="bg-white rounded-lg shadow">
        {filteredSessions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Session Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Attendance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Engagement
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Topics
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSessions.map(session => {
                  const attendanceRate = session.expectedLearners > 0 
                    ? (session.actualLearners / session.expectedLearners) * 100 
                    : 0;

                  return (
                    <tr key={session.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium text-gray-900">
                              {session.subject} - Grade {session.grade}
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              session.sessionType === 'Regular Class' ? 'bg-blue-100 text-blue-800' :
                              session.sessionType === 'Tutorial' ? 'bg-purple-100 text-purple-800' :
                              session.sessionType === 'Assessment' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {session.sessionType}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                            <Calendar className="w-4 h-4" />
                            <span>{new Date(session.sessionDate).toLocaleDateString()}</span>
                            {session.sessionTime && (
                              <>
                                <Clock className="w-4 h-4 ml-2" />
                                <span>{session.sessionTime}</span>
                              </>
                            )}
                            {session.duration && (
                              <span className="ml-2">({session.duration}min)</span>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {session.actualLearners} / {session.expectedLearners}
                            </div>
                            <div className={`text-sm font-medium ${
                              attendanceRate >= 90 ? 'text-green-600' :
                              attendanceRate >= 75 ? 'text-yellow-600' :
                              'text-red-600'
                            }`}>
                              {attendanceRate.toFixed(1)}%
                            </div>
                          </div>
                          <div className="w-16">
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${
                                  attendanceRate >= 90 ? 'bg-green-500' :
                                  attendanceRate >= 75 ? 'bg-yellow-500' :
                                  'bg-red-500'
                                }`}
                                style={{ width: `${Math.min(attendanceRate, 100)}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          session.learnerEngagement === 'High' ? 'bg-green-100 text-green-800' :
                          session.learnerEngagement === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                          session.learnerEngagement === 'Low' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {session.learnerEngagement || 'Not Set'}
                        </span>
                      </td>

                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {session.topicsCovered?.slice(0, 2).join(', ')}
                          {session.topicsCovered?.length > 2 && (
                            <span className="text-gray-500">
                              {' '}+{session.topicsCovered.length - 2} more
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => onView(session)}
                            className="text-blue-600 hover:text-blue-800"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => onEdit(session)}
                            className="text-gray-600 hover:text-gray-800"
                            title="Edit Session"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(session.id, `${session.subject} - ${new Date(session.sessionDate).toLocaleDateString()}`)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete Session"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Attendance Sessions</h3>
            <p className="text-gray-500 mb-6">
              Start by recording your first class attendance session.
            </p>
            <button
              onClick={onCreateNew}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-blue-700"
            >
              Record First Session
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceList;